/**
 * Cookie consent banner.
 *
 * Appears on first visit. Stores the user's choice in localStorage under
 * "analytics_consent". On accept, loads GA4 and Clarity scripts. On decline,
 * no analytics scripts are loaded (Sentry is separate — it runs as a
 * legitimate-interest tool with no PII).
 *
 * Consent can be withdrawn at any time: the footer "Cookie Preferences" link
 * dispatches an `open-consent-banner` event to re-open this banner. Declining
 * after previously accepting reloads the page so already-injected analytics
 * scripts stop running immediately.
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { getConsentChoice, setConsentChoice } from "@/lib/analytics";
import { loadAnalyticsAfterConsent } from "@/lib/thirdParty";

export const OPEN_CONSENT_EVENT = "open-consent-banner";

/** Re-open the consent banner (e.g. from a footer "Cookie Preferences" link). */
export function openConsentBanner(): void {
  window.dispatchEvent(new CustomEvent(OPEN_CONSENT_EVENT));
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // If the user already chose, respect it and load GA4+Clarity if accepted
    const prior = getConsentChoice();
    if (prior === "accepted") {
      loadAnalyticsAfterConsent();
    } else if (prior === null) {
      // No choice yet — show the banner
      setVisible(true);
    }

    // Allow re-opening the banner to change/withdraw consent at any time
    const onOpen = () => setVisible(true);
    window.addEventListener(OPEN_CONSENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, onOpen);
  }, []);

  const accept = () => {
    setConsentChoice("accepted");
    loadAnalyticsAfterConsent();
    setVisible(false);
  };

  const decline = () => {
    const hadAccepted = getConsentChoice() === "accepted";
    setConsentChoice("declined");
    setVisible(false);
    // If analytics scripts were already injected, reload so they stop running
    if (hadAccepted) {
      window.location.reload();
    }
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-5 bg-card border-t border-border shadow-2xl shadow-black/10 animate-in slide-in-from-bottom-2 duration-300"
      data-testid="consent-banner"
    >
      <div className="container mx-auto max-w-5xl flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <p className="flex-1 text-sm text-muted-foreground leading-relaxed">
          We use analytics cookies (Google Analytics, Microsoft Clarity) to understand how visitors
          use Souq Crypto, and Sentry for error monitoring. Analytics cookies require your consent;
          error monitoring runs without it as it collects no personal data. You can change your
          choice at any time via "Cookie Preferences" in the footer.{" "}
          <a href="/privacy" className="underline underline-offset-2 hover:text-foreground transition-colors">
            Privacy Policy
          </a>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" onClick={accept} data-testid="btn-consent-accept">
            Accept
          </Button>
          <Button size="sm" variant="outline" onClick={decline} data-testid="btn-consent-decline">
            Decline
          </Button>
          <button
            onClick={decline}
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
            data-testid="btn-consent-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
